import React, { FormEvent, useContext, useEffect, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Text, Notification, Input } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { Page } from 'styles';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { BackLink } from 'components/back-link';
import { UPDATE_NICKNAME, GET_NICKNAME } from './nickname.api';

const getCopy = copyFor('settings.membership.nickname');
const getServerError = copyFor('errors.server');
const { membership } = routes();

interface Props {
  match: {
    params: {
      planId: string;
    };
  };
}

export const Nickname = (props: Props) => {
  const history = useHistory();
  const { addNotification } = useContext(Notification.Context);
  const planId = Number(props.match.params.planId);
  const { data, loading, error } = useQuery<
    GetNicknameQuery,
    GetNicknameQueryVariables
  >(GET_NICKNAME, {
    skip: !planId,
    variables: { planId: planId || -1 },
  });

  const name = data?.plan.name;
  const [nickname, setNickname] = useState('');

  useEffect(() => {
    if (name) {
      setNickname(name);
    }
  }, [name]);

  const [mutate] = useMutation(UPDATE_NICKNAME);

  const [{ error: mutationError, loading: mutationLoading }, submit] =
    useAsyncFn(async () => {
      await mutate({
        variables: {
          id: planId,
          input: {
            name: nickname,
          },
        },
      });
      addNotification({
        displayTime: NOTIFICATION_DISPLAY_TIME,
        message: getCopy('toastNotification'),
        variant: 'success',
      });
    }, [mutate, planId, nickname, addNotification]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await submit();
    history.push(membership);
  };

  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage />;

  return (
    <>
      <BackLink to={membership} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h1" margin={{ bottom: '48px' }}>
          {getCopy('title')}
        </Text>
        <form onSubmit={handleSubmit}>
          <Input
            label={getCopy('label')}
            margin={{ bottom: '16px' }}
            name="nickname"
            onChange={setNickname}
            value={nickname}
          />
          {mutationError && (
            <Alert margin={{ top: '32px' }}>{getServerError('generic')}</Alert>
          )}
          <Button
            disabled={!nickname || nickname === name}
            fullWidth
            margin={{ top: '32px' }}
            loading={mutationLoading}
            type="submit"
          >
            {getCopy('button')}
          </Button>
        </form>
      </Page>
    </>
  );
};
